import { useState } from 'react';
import { useIsAdmin } from '../auth/hooks/useIsAdmin';

import ShareWallsDialog from './ShareWallsDialog';

import {
  Box,
  Button,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Typography,
  ListSubheader,
  FormControlLabel,
} from '@mui/material';
import { useAppSelector } from '../../app/hooks';
import { grey } from '@mui/material/colors';

import { useWalls } from './api/getWalls';
import PublicIcon from '@mui/icons-material/Public';

const ShareWallsSelectionPage = () => {
  const { user } = useAppSelector((state) => state.auth);

  const [selectedWallIds, setSelectedWallIds] = useState<number[]>([]);
  const { data: walls } = useWalls(user?.id || -1);
  const unSynchedWalls = walls?.filter((wall) => !wall.synched);
  const appSelectedWallId = useAppSelector(
    (state) => state.walls.selectedWallId
  );

  const isAdmin = useIsAdmin();

  const [wallShareDialogOpen, setWallShareDialogOpen] = useState(false);

  const handleWallClicked = (wallId: number, synched: boolean | undefined) => {
    if (synched) return;

    if (selectedWallIds.includes(wallId))
      setSelectedWallIds(selectedWallIds.filter((id) => id !== wallId));
    else setSelectedWallIds((priorIds) => [...priorIds, wallId]);
  };

  const handleSelectAllWalls = () => {
    const wallIds = unSynchedWalls?.map((wall) => wall.id);
    if (!wallIds) return;
    if (selectedWallIds.length !== wallIds.length) {
      setSelectedWallIds(wallIds);
    } else {
      setSelectedWallIds([]);
    }
  };

  const handleShareDialogClosed = () => {
    setWallShareDialogOpen(false);
  };

  return (
    <>
      {isAdmin && (
        <ShareWallsDialog
          open={wallShareDialogOpen}
          handleClose={handleShareDialogClosed}
          selectedWallIds={selectedWallIds}
        />
      )}
      <Grid container spacing={2}>
        <Grid item container>
          <Grid item component={Paper} xs>
            <List sx={{ overflow: 'auto', maxHeight: '80vh' }} dense>
              <ListSubheader>Select Walls To Share</ListSubheader>
              <ListItem>
                <FormControlLabel
                  control={
                    <Checkbox
                      onChange={handleSelectAllWalls}
                      checked={
                        selectedWallIds.length === unSynchedWalls?.length
                      }
                      indeterminate={
                        selectedWallIds.length > 0 &&
                        selectedWallIds.length !== unSynchedWalls?.length
                      }
                    />
                  }
                  label='Select All'
                />
              </ListItem>
              <Box sx={{ ml: 3 }}>
                {walls?.map((wall) => (
                  <ListItem
                    key={wall.id}
                    button
                    selected={appSelectedWallId === wall.id}
                    onClick={() => handleWallClicked(wall.id, wall?.synched)}
                  >
                    <ListItemIcon>
                      {!wall.synched ? (
                        <Checkbox
                          edge='start'
                          tabIndex={-1}
                          checked={selectedWallIds.includes(wall.id)}
                        />
                      ) : (
                        <PublicIcon />
                      )}
                    </ListItemIcon>
                    <ListItemText>{wall.name}</ListItemText>
                  </ListItem>
                ))}
              </Box>
            </List>
            {!walls ||
              (walls.length === 0 && (
                <Typography
                  color={grey[500]}
                  sx={{ pt: '2rem', pl: '2rem', pb: '2rem' }}
                >
                  No walls found.
                </Typography>
              ))}
          </Grid>
        </Grid>
        <Grid container item xs={12} spacing={1} justifyContent='flex-end'>
          {isAdmin && (
            <Grid item>
              <Button
                variant='contained'
                color='primary'
                disabled={selectedWallIds.length < 1}
                onClick={() => setWallShareDialogOpen(true)}
              >
                Share Selected Walls
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default ShareWallsSelectionPage;
